/**
 * Job planner (project dashboard → Plan the job): phases, tasks with estimates, materials, the
 * daily setup / cleanup checklists and the days you're on it — turned into the writes that make
 * them real. Pure: ids come from `newId`, the caller commits everything in one batch.
 *
 * draft = {
 *   phases:    [{ key, id?, title }]              // id = an existing section (title may be renamed)
 *   tasks:     [{ phaseKey|null, title, estimatedMinutes? }]
 *   materials: [{ title }]                          // → tasks tagged 'material' under "Materials & pickup"
 *   setupSteps / cleanupSteps: [{ title, estimatedMinutes? }] | null   // null = leave the project's as is
 *   startDate, endDate: 'YYYY-MM-DD' | null
 *   days: ['YYYY-MM-DD'], dayStart / dayEnd: minutes after midnight   // → one time block per day
 * }
 */

export const MATERIALS_PHASE = 'Materials & pickup';
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const clean = (s) => String(s ?? '').trim();

/** Every date from start to end inclusive (max 120), local calendar days. */
export function datesBetween(start, end) {
  if (!DAY.test(start || '') || !DAY.test(end || '') || end < start) return DAY.test(start || '') ? [start] : [];
  const out = [];
  const d = new Date(`${start}T12:00:00`);
  while (out.length < 120) {
    const s = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (s > end) break;
    out.push(s);
    d.setDate(d.getDate() + 1);
  }
  return out;
}

/** Mon–Fri of a range — the default days on a job. */
export function weekdaysBetween(start, end) {
  return datesBetween(start, end).filter((s) => { const w = new Date(`${s}T12:00:00`).getDay(); return w !== 0 && w !== 6; });
}

const steps = (list) => (Array.isArray(list) ? list : [])
  .map((s) => (typeof s === 'string' ? { title: s } : s))
  .filter((s) => s && clean(s.title))
  .map((s) => ({ title: clean(s.title), estimatedMinutes: Number(s.estimatedMinutes) > 0 ? Math.round(Number(s.estimatedMinutes)) : null }));

/**
 * @returns {{ sections: object[], sectionUpdates: {id,title}[], tasks: object[], blocks: object[], projectPatch: object|null }}
 *   sections / blocks are full docs; tasks are task FIELDS (the app's makeTaskDoc fills the rest).
 */
export function planJobWrites({ project, sections = [], tasks = [], blocks = [], draft, newId, now = Date.now() }) {
  const out = { sections: [], sectionUpdates: [], tasks: [], blocks: [], projectPatch: null };
  if (!project?.id || !draft) return out;
  const pid = project.id;
  const mine = sections.filter((s) => s.projectId === pid);
  let order = mine.reduce((m, s) => Math.max(m, Number(s.order) || 0), 0);
  const idOf = new Map();                                  // phase key → section id

  for (const ph of draft.phases || []) {
    const title = clean(ph.title);
    if (ph.id) {
      idOf.set(ph.key, ph.id);
      const was = mine.find((s) => s.id === ph.id);
      if (was && title && title !== clean(was.title)) out.sectionUpdates.push({ id: ph.id, title });
      continue;
    }
    if (!title) continue;
    const id = newId();
    idOf.set(ph.key, id);
    out.sections.push({ id, projectId: pid, title, description: '', order: ++order, status: 'active', color: null, completedAt: null, createdAt: now + out.sections.length });
  }

  // Next sortOrder in a section, after what's already there.
  const next = new Map();
  const sortAfter = (sectionId) => {
    const k = sectionId || '';
    if (!next.has(k)) next.set(k, tasks.filter((t) => t.projectId === pid && (t.sectionId || '') === k && !t.parentId && !t.bucket).reduce((m, t) => Math.max(m, Number(t.sortOrder) || 0), 0));
    const v = next.get(k) + 1000;
    next.set(k, v);
    return v;
  };

  for (const t of draft.tasks || []) {
    const title = clean(t.title);
    if (!title) continue;
    const sectionId = t.phaseKey ? idOf.get(t.phaseKey) || null : null;
    out.tasks.push({
      title, projectId: pid, sectionId, parentId: null, scheduledDate: null,
      estimatedMinutes: Number(t.estimatedMinutes) > 0 ? Math.round(Number(t.estimatedMinutes)) : null,
      sortOrder: sortAfter(sectionId),
    });
  }

  const mats = (draft.materials || []).map((m) => clean(m.title)).filter(Boolean);
  if (mats.length) {
    let matId = mine.find((s) => clean(s.title).toLowerCase() === MATERIALS_PHASE.toLowerCase())?.id
      || out.sections.find((s) => s.title.toLowerCase() === MATERIALS_PHASE.toLowerCase())?.id;
    if (!matId) {
      matId = newId();
      // First in the outline: you buy before you build.
      const first = Math.min(0, ...mine.map((s) => Number(s.order) || 0), ...out.sections.map((s) => s.order)) - 1;
      out.sections.push({ id: matId, projectId: pid, title: MATERIALS_PHASE, description: '', order: first, status: 'active', color: null, completedAt: null, createdAt: now + out.sections.length });
    }
    for (const title of mats) {
      out.tasks.push({ title, projectId: pid, sectionId: matId, parentId: null, scheduledDate: null, estimatedMinutes: null, tags: ['material'], sortOrder: sortAfter(matId) });
    }
  }

  const start = Number.isFinite(draft.dayStart) ? draft.dayStart : 8 * 60;
  const end = Number.isFinite(draft.dayEnd) && draft.dayEnd > start ? draft.dayEnd : start + 8 * 60;
  const booked = new Set(blocks.filter((b) => b.projectId === pid).map((b) => b.date));
  for (const date of [...new Set(draft.days || [])].filter((d) => DAY.test(d)).sort()) {
    if (booked.has(date)) continue;
    out.blocks.push({
      id: newId(), title: project.name || '', projectId: pid, date, kind: 'job-day', departmentId: project.departmentId || null,
      start, end,
      startHour: Math.floor(start / 60), startMinute: start % 60, endHour: Math.floor(end / 60), endMinute: end % 60,
      color: project.color || '#3B82F6', createdAt: now + out.blocks.length,
    });
  }

  const patch = {};
  const sd = DAY.test(draft.startDate || '') ? draft.startDate : null;
  const ed = DAY.test(draft.endDate || '') ? draft.endDate : null;
  if (sd && sd !== project.startDate) patch.startDate = sd;
  if (ed && ed !== project.endDate) patch.endDate = ed;
  if (Array.isArray(draft.setupSteps)) patch.setupSteps = steps(draft.setupSteps);
  if (Array.isArray(draft.cleanupSteps)) patch.cleanupSteps = steps(draft.cleanupSteps);
  out.projectPatch = Object.keys(patch).length ? patch : null;
  return out;
}
